import { generateClient, GraphQLResult } from "aws-amplify/api";
import { FieldTemplate, FormGroup } from "db/definitions";
// @ts-ignore
import { batchGetFormGroupsThatArePhotologs } from "../graphql/queries";
import _getProject from "./_getProject";
import batchRetrievePhotoLogs from "./batchRetrievePhotoLogs";
import getTableFields from "./getTableFields";
import { batchGetOfflinePhotologs } from "db/queries/formGroupQueries";

/**
 * determines which photologs in the current project it can use to match to the current fieldtemplate.
 * @param id for the project
 * @param imageRollTemplate for the row that we need to pull images into
 * @param online status
 * @returns photologs
 */
async function _getPhotoLogsByProject(id: string, imageRollTemplate: FieldTemplate, online: boolean): Promise<FormGroup[]> {
	if (imageRollTemplate.tableFields) {
		// first get the project.
		const projectPromise = _getProject(id, online);
		let imageRollTemplateTableFields: FieldTemplate[] = [];

		const potentialTemplateImageRollField = imageRollTemplate.tableFields.items.filter((template) => {
			if (template.inputType === "imageroll") {
				return template;
			}
		});

		if (potentialTemplateImageRollField.length > 0) {
			for (let i = 0; i < potentialTemplateImageRollField.length; i++) {
				const imageRollField = potentialTemplateImageRollField[i];
				if (imageRollField.tableFields) {
					for (const tableField of imageRollField.tableFields.items) {
						imageRollTemplateTableFields.push(tableField);
					}
				}
			}
		}
		const project = await projectPromise;
		if (project) {
			// loop through and filter out all the form groups that A. are type photolog
			// the photolog form group has the same image roll template as this field..
			const formGroupIdArray = [];
			for (let i = 0; i < project.formGroups.items.length; i++) {
				const formGroup = project.formGroups.items[i];
				if (formGroup.type === "photolog") {
					// loop through the forminstances and check each ones' formTemplate.fieldsOrder if it has
					// the id for this field's template
					formGroupIdArray.push(formGroup.id);
				}
			}

			if (formGroupIdArray.length === 0) {
				return [];
			}

			const photoLogIds = [];
			let formGroupsThatArePhotoLogs: FormGroup[] = [];
			if (online) {
				interface QueryResponse {
					batchGetFormGroups: FormGroup[];
				}
				const client = generateClient({ authMode: "userPool" });
				const response = (await client.graphql({
					query: batchGetFormGroupsThatArePhotologs,
					authMode: "userPool",
					variables: { ids: formGroupIdArray },
				})) as GraphQLResult<QueryResponse>;
				formGroupsThatArePhotoLogs = response.data.batchGetFormGroups;
			} else {
				formGroupsThatArePhotoLogs = await batchGetOfflinePhotologs(formGroupIdArray);
			}

			for (const formGroup of formGroupsThatArePhotoLogs) {
				if (formGroup.formInstances) {
					for (let i = 0; i < formGroup.formInstances.items.length; i++) {
						const formInstance = formGroup.formInstances.items[i];
						let currentPhotoLogTableFields: FieldTemplate[] = [];
						const potentialImageRollField = formInstance.formTemplate?.fieldTemplates?.items.filter((template) => {
							if (template.inputType === "imageroll") return template;
						});

						if (potentialImageRollField && potentialImageRollField.length > 0) {
							for (let i = 0; i < potentialImageRollField.length; i++) {
								const imageRollField = potentialImageRollField[i];
								if (imageRollField.tableFields) {
									for (const tableField of imageRollField.tableFields.items) {
										currentPhotoLogTableFields.push(tableField);
									}
								}
							}
						}

						const hasPhotoRollConnection = compareFieldTemplatesForTwoInstances(imageRollTemplateTableFields, currentPhotoLogTableFields);
						if (hasPhotoRollConnection) {
							photoLogIds.push(formGroup.id);
							break;
						}
					}
				}
			}

			if (!photoLogIds || photoLogIds.length < 1) {
				return [];
			}
			// 7 seconds to get here
			// get all the photolog form groups
			const formGroups = await batchRetrievePhotoLogs(photoLogIds, online);
			const final: FormGroup[] = [];
			// Now need to go into the imageroll field and make sure to get all its table fields
			for (let i = 0; i < formGroups.length; i++) {
				const formGroup = formGroups[i];
				if (formGroup.formInstances) {
					for (let f = 0; f < formGroup.formInstances.items.length; f++) {
						const formInstance = formGroup.formInstances.items[f];

						// might need to check for nextToken here.. but at the moment we dont have anything that would need to

						for (let j = 0; j < formInstance.fieldInstances.items.length; j++) {
							const fieldInstance = formInstance.fieldInstances.items[j];
							if (fieldInstance.fieldTemplate?.inputType === "imageroll") {
								// this the one
								const tableFields = await getTableFields(fieldInstance.id, online);
								fieldInstance.tableFields = {
									items: tableFields,
								};
							}
						}
					}
					final.push(formGroup);
				}
			}
			return final;
		}
	}
	return [];
}

function compareFieldTemplatesForTwoInstances(firstArray: FieldTemplate[], secondArray: FieldTemplate[]): boolean {
	const hasTheSameRollLinkId = firstArray.every((fieldTemplate) => {
		secondArray.some((fieldTemplate2) => fieldTemplate.rollLinkId === fieldTemplate2.rollLinkId);
	});

	return hasTheSameRollLinkId;
}

export default _getPhotoLogsByProject;
